/**
 * County letterhead PDFs for Payroll Reconciliation reports.
 * Matches docus "Spending entity (Costings) Summary" / wage bill layouts.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { county_logo, base as apiBase } from "@/api";

function formatMoney(n) {
  const x = Number(n) || 0;
  return x.toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export async function loadImageDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { credentials: "include", cache: "force-cache" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
}

/**
 * Load county / configured logos as data URLs for jsPDF.
 * Returns primary logo data URL. Also attaches secondary on letterhead._logoSecondaryDataUrl
 * so addCountyLetterhead can place both seals without every caller changing.
 * @param {object} [letterhead] from getCompanyInfo
 * @returns {Promise<string|null>}
 */
export async function loadCountyLogoDataUrl(letterhead = null) {
  const pair = await loadCountyLogoPair(letterhead);
  if (letterhead && typeof letterhead === "object") {
    // eslint-disable-next-line no-param-reassign
    letterhead._logoSecondaryDataUrl = pair.secondary || null;
    // eslint-disable-next-line no-param-reassign
    letterhead._logoPrimaryDataUrl = pair.primary || null;
  }
  return pair.primary || pair.secondary || null;
}

/**
 * Load primary + secondary logos for dual-seal letterheads.
 * @returns {Promise<{ primary: string|null, secondary: string|null }>}
 */
export async function loadCountyLogoPair(letterhead = null) {
  const primaryCandidates = [];
  const secondaryCandidates = [];
  if (letterhead?.logoPrimaryUrl) primaryCandidates.push(letterhead.logoPrimaryUrl);
  if (letterhead?.logoSecondaryUrl) secondaryCandidates.push(letterhead.logoSecondaryUrl);
  // fall back primary chain
  if (!primaryCandidates.length && letterhead?.logoSecondaryUrl) {
    primaryCandidates.push(letterhead.logoSecondaryUrl);
  }
  primaryCandidates.push(county_logo);
  if (apiBase) {
    primaryCandidates.push(`${String(apiBase).replace(/\/$/, "")}/logos/logo.png`);
  }

  let primary = null;
  for (const url of primaryCandidates) {
    primary = await loadImageDataUrl(url);
    if (primary) break;
  }
  let secondary = null;
  for (const url of secondaryCandidates) {
    secondary = await loadImageDataUrl(url);
    if (secondary) break;
  }
  return { primary, secondary };
}

/**
 * Draw formal county letterhead from admin company / contacts settings.
 * Supports logo position (left / center / right) and caption under each logo.
 * Returns Y position for content start.
 */
export function addCountyLetterhead(doc, options = {}) {
  const {
    title = "PAYROLL RECONCILIATION REPORT",
    subtitle = "",
    periodLabel = "",
    logoDataUrl = null,
    logoSecondaryDataUrl = null,
    letterhead = null,
    countyName = null,
    departmentLine = null,
  } = options;

  const lh = letterhead || {};
  const name =
    countyName ||
    lh.companyName ||
    "COUNTY GOVERNMENT OF HOMA BAY";
  const deptLine =
    departmentLine ||
    lh.departmentLine ||
    "Department of Public Service Management & Performance Contracting — HRMIS";

  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  const logoW = 22;
  const logoH = 22;
  let y = 10;

  const primaryLogo = logoDataUrl || lh._logoPrimaryDataUrl || null;
  const secondaryLogo = logoSecondaryDataUrl || lh._logoSecondaryDataUrl || null;
  const pPos = String(lh.logoPrimaryPosition || "left").toLowerCase();
  const sPos = String(lh.logoSecondaryPosition || "right").toLowerCase();
  const pCap = String(lh.logoPrimaryCaption || "").trim();
  const sCap = String(lh.logoSecondaryCaption || "").trim();

  const xFor = (pos) => {
    if (pos === "right") return pageW - margin - logoW;
    if (pos === "center") return pageW / 2 - logoW / 2;
    return margin;
  };

  const drawLogoBlock = (dataUrl, pos, caption) => {
    if (!dataUrl) return 0;
    let used = logoH;
    try {
      const fmt = String(dataUrl).includes("image/png")
        ? "PNG"
        : String(dataUrl).includes("image/webp")
          ? "WEBP"
          : "JPEG";
      const x = xFor(pos);
      doc.addImage(dataUrl, fmt, x, y, logoW, logoH);
      if (caption) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(15, 23, 42);
        const cx = x + logoW / 2;
        const lines = doc.splitTextToSize(caption, logoW + 18);
        doc.text(lines, cx, y + logoH + 3.5, { align: "center" });
        used = logoH + 3.5 + lines.length * 3.2;
      }
    } catch (_) {
      /* skip broken image */
    }
    return used;
  };

  let logoBlockH = 0;
  // If both logos exist, place independently so left+right dual seal works
  if (primaryLogo && secondaryLogo) {
    const h1 = drawLogoBlock(primaryLogo, pPos === sPos ? "left" : pPos, pCap);
    const h2 = drawLogoBlock(secondaryLogo, pPos === sPos ? "right" : sPos, sCap);
    logoBlockH = Math.max(h1, h2);
  } else if (primaryLogo) {
    logoBlockH = drawLogoBlock(primaryLogo, pPos, pCap);
  } else if (secondaryLogo) {
    logoBlockH = drawLogoBlock(secondaryLogo, sPos, sCap);
  }

  if (logoBlockH > 0) {
    y += logoBlockH + 3;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(String(name).toUpperCase(), pageW / 2, y, { align: "center" });
  y += 5;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);
  doc.text(String(deptLine), pageW / 2, y, { align: "center" });
  y += 4;

  const contactBits = [];
  if (lh.companyAddress) contactBits.push(String(lh.companyAddress));
  if (lh.companyContact) contactBits.push(`Tel: ${lh.companyContact}`);
  if (lh.companyEmail) contactBits.push(String(lh.companyEmail));
  if (lh.website) contactBits.push(String(lh.website));
  if (contactBits.length) {
    doc.setFontSize(7);
    doc.text(contactBits.join("  ·  "), pageW / 2, y, { align: "center" });
    y += 4;
  }

  doc.setDrawColor(12, 74, 110);
  doc.setLineWidth(0.6);
  doc.line(14, y, pageW - 14, y);
  y += 2;
  doc.setDrawColor(180, 83, 9);
  doc.setLineWidth(0.3);
  doc.line(14, y, pageW - 14, y);
  y += 7;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(String(title).toUpperCase(), pageW / 2, y, { align: "center" });
  y += 5;

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(51, 65, 85);
    doc.text(String(subtitle), pageW / 2, y, { align: "center" });
    y += 5;
  }

  if (periodLabel) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(12, 74, 110);
    doc.text(`Period: ${periodLabel}`, pageW / 2, y, { align: "center" });
    y += 5;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(`Generated: ${new Date().toLocaleString("en-KE")}`, pageW / 2, y, { align: "center" });
  y += 7;

  return y;
}

function addFooter(doc, letterhead = null) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const footerText =
    (letterhead && letterhead.reportFooter) ||
    "Official HRMIS document — County Government of Homa Bay";
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(String(footerText), 14, pageH - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageW - 14, pageH - 8, { align: "right" });
  }
}

function safeFilePart(s) {
  return String(s || "report")
    .replace(/[^\w-]+/g, "_")
    .slice(0, 80);
}

/**
 * Generic branded table PDF.
 */
export function exportBrandedTablePdf(opts) {
  const orientation = opts.orientation || "portrait";
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const startY = addCountyLetterhead(doc, {
    title: opts.title,
    subtitle: opts.subtitle || "",
    periodLabel: opts.periodLabel || "",
    logoDataUrl: opts.logoDataUrl || null,
    logoSecondaryDataUrl: opts.logoSecondaryDataUrl || null,
    letterhead: opts.letterhead || null,
  });

  autoTable(doc, {
    startY,
    head: [opts.head || []],
    body: opts.body || [],
    styles: { fontSize: 7, cellPadding: 1.5, overflow: "linebreak" },
    headStyles: {
      fillColor: [12, 74, 110],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 7,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 12, right: 12 },
  });

  addFooter(doc, opts.letterhead || null);
  const name =
    opts.filename ||
    `${safeFilePart(opts.title)}_${safeFilePart(opts.periodLabel)}.pdf`;
  doc.save(name);
  return doc;
}

/**
 * Spending Entity (Costings) Summary matching docus template —
 * full table + TOTALS + TOTAL WAGE BILL bottom breakdown (monthly / annual).
 */
export function exportSpendingEntityCostingsPdf(opts) {
  const {
    periodLabel = "",
    logoDataUrl = null,
    letterhead = null,
    rows = [],
    totals = {},
    bottom = null,
    filename = null,
  } = opts;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const county = (letterhead && letterhead.companyName) || "COUNTY GOVERNMENT OF HOMA BAY";
  const startY = addCountyLetterhead(doc, {
    title: "SPENDING ENTITY (COSTINGS) SUMMARY",
    subtitle: `HRIS-Ke ${periodLabel} — by Register department / county spending entity`,
    periodLabel,
    logoDataUrl,
    letterhead,
    countyName: county,
  });

  const fm = (n) => formatMoney(n);
  const body = (rows || []).map((r) => [
    r.seq,
    r.vote || r.voteCode || "",
    r.entity,
    r.staffCount,
    fm(r.basic),
    fm(r.rental),
    fm(r.commuter),
    fm(r.otherAllow),
    fm(r.gross),
    fm(r.nssf),
    fm(r.housing),
    fm(r.pensionEmployer != null ? r.pensionEmployer : r.pension902),
    fm(r.nita),
    fm(r.total),
  ]);

  if (totals && Object.keys(totals).length) {
    body.push([
      "",
      "",
      "TOTALS",
      totals.staffCount || 0,
      fm(totals.basic),
      fm(totals.rental),
      fm(totals.commuter),
      fm(totals.otherAllow),
      fm(totals.gross),
      fm(totals.nssf),
      fm(totals.housing),
      fm(totals.pensionEmployer),
      fm(totals.nita),
      fm(totals.total),
    ]);
  }

  autoTable(doc, {
    startY,
    head: [[
      "S.No.",
      "VOTE",
      "COUNTY SPENDING ENTITY",
      "STAFF EARNING",
      "Basic Pay",
      "Rental House All.",
      "Commuter All.",
      "Other Allowances",
      "GROSS SALARY",
      "NSSF",
      "HOUSING LEVY",
      "PENSION (EMPLOYER)",
      "NITA",
      "TOTAL",
    ]],
    body,
    styles: { fontSize: 6.5, cellPadding: 1.2, overflow: "linebreak" },
    headStyles: { fillColor: [12, 74, 110], textColor: 255, fontStyle: "bold", fontSize: 6.5 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 10, right: 10 },
    didParseCell(data) {
      if (data.section === "body" && data.row.index === body.length - 1 && totals) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [226, 232, 240];
      }
    },
  });

  // Bottom TOTAL WAGE BILL block (docus template)
  if (bottom && bottom.monthly) {
    const m = bottom.monthly;
    const a = bottom.annual || {};
    let y = (doc.lastAutoTable && doc.lastAutoTable.finalY) || startY;
    y += 8;
    const pageH = doc.internal.pageSize.getHeight();
    if (y > pageH - 55) {
      doc.addPage();
      y = 18;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text("TOTAL WAGE BILL:", 14, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [["", "MONTHLY", "ANNUAL PROJECTION"]],
      body: [
        ["TOTAL GROSS SALARY", fm(m.gross), fm(a.gross)],
        ["EMPLOYER OBLIGATIONS", "", ""],
        ["    NSSF", fm(m.nssf), fm(a.nssf)],
        ["    HOUSING LEVY", fm(m.housing), fm(a.housing)],
        ["    PENSION (EMPLOYER)", fm(m.pensionEmployer), fm(a.pensionEmployer)],
        ["    NITA", fm(m.nita), fm(a.nita)],
        ["    GRATUITY", fm(m.gratuity), fm(a.gratuity)],
        ["TOTAL EMPLOYER OBLIGATIONS", fm(m.employerObligations), fm(a.employerObligations)],
        [`TOTAL ${periodLabel.toUpperCase()} WAGE BILL`, fm(m.totalWageBill), fm(a.totalWageBill)],
      ],
      styles: { fontSize: 8, cellPadding: 1.8 },
      headStyles: { fillColor: [12, 74, 110], textColor: 255, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { halign: "right", cellWidth: 45 },
        2: { halign: "right", cellWidth: 50 },
      },
      margin: { left: 14, right: 14 },
      didParseCell(data) {
        if (data.section !== "body") return;
        const label = String(data.row.raw[0] || "");
        if (label === "EMPLOYER OBLIGATIONS" || label.startsWith("TOTAL ")) {
          data.cell.styles.fontStyle = "bold";
        }
        if (label.includes("WAGE BILL")) {
          data.cell.styles.fillColor = [224, 242, 254];
        }
      },
    });
  }

  addFooter(doc, letterhead);
  doc.save(filename || `spending_entity_costings_${safeFilePart(periodLabel)}.pdf`);
  return doc;
}

export { formatMoney };
